/**
 * The combat half of the HUD, as plain strings and numbers: where the enemy is,
 * how the player's aeroplane is holding up, and whether the fight is over.
 *
 * Pure, like `hud-snapshot.ts` — a Solid component renders it and a unit test
 * asserts it, and neither needs a GPU. Bearings are horizon-referenced (the
 * engine's `contactReport`, not the body-frame geometry the AI flies on), so
 * the arrow does not spin when the player rolls.
 */

import {
  contactReport,
  mpsToMph,
  radiansToDegrees,
  type ContactReport,
  type EncounterState,
  type EncounterStatus,
  type Vec3,
} from "@red-opus/engine";
import { formatAltitudeFt } from "./hud-format.ts";

export interface EnemyContact {
  readonly id: string;
  /** "320 yd" — the original's gunnery talk is in yards, not metres. */
  readonly range: string;
  readonly rangeM: number;
  /** "2 o'clock", the way a pilot calls a contact. */
  readonly clock: string;
  /** "HIGH" / "LEVEL" / "LOW". */
  readonly relativeHeight: string;
  /** Relative bearing in degrees, 0 = ahead, +90 = right — for an edge arrow. */
  readonly bearingDeg: number;
  readonly behind: boolean;
  /** Closing speed in mph; negative when the contact is pulling away. */
  readonly closureMph: number;
  readonly burning: boolean;
  readonly destroyed: boolean;
}

/**
 * The rack, for an aeroplane that has one. `undefined` for every aeroplane that
 * does not, which is all of them unless the mission handed the player bombs —
 * a port extension (docs/game/player-bombing.md), so the HUD grows a counter
 * only on the missions that actually gave him something to count.
 *
 * `released` is kept alongside `remaining` so the counter stays on screen at
 * `0 of 4` after the last one has gone rather than vanishing at the moment the
 * player most wants to know he is empty.
 */
export interface BombLoadHud {
  readonly remaining: number;
  readonly released: number;
}

export interface CombatHud {
  readonly enemy: EnemyContact | undefined;
  readonly status: EncounterStatus;
  /** The banner text once the fight is decided, or `undefined` while it runs. */
  readonly banner: string | undefined;
  /** 0..1 worst component health on the player's aeroplane. */
  readonly airframe: number;
  readonly wounded: boolean;
  readonly burning: boolean;
  readonly hitsScored: number;
  readonly hitsTaken: number;
  /** The gunfire half of `hitsTaken`, archie excluded — what marks the painted cockpit (`cockpit-hits.ts`). */
  readonly gunHitsTaken: number;
  /** What is left under the wings, or `undefined` when the player went up with guns only. */
  readonly bombs: BombLoadHud | undefined;
}

export const METRES_PER_YARD = 0.9144;

/** Bands within which a contact counts as level with the player rather than high or low. */
export const LEVEL_HEIGHT_BAND_M = 60;

export function formatRangeYards(rangeM: number): string {
  return `${Math.round(rangeM / METRES_PER_YARD).toLocaleString("en-US")} yd`;
}

export function relativeHeightLabel(heightDifferenceM: number): string {
  if (heightDifferenceM > LEVEL_HEIGHT_BAND_M) return "HIGH";
  if (heightDifferenceM < -LEVEL_HEIGHT_BAND_M) return "LOW";
  return "LEVEL";
}

export function clockLabel(clockPosition: number): string {
  return `${clockPosition} o'clock`;
}

/**
 * The outcome banners. `ENEMY DESTROYED` is the original's own wording for a
 * kill; the other two are port-side lines in the same voice.
 */
export const OUTCOME_BANNERS: Readonly<Record<EncounterStatus, string | undefined>> = {
  active: undefined,
  enemyDown: "ENEMY DESTROYED",
  playerDown: "YOU'VE BEEN SHOT DOWN",
  mutualDestruction: "YOU WENT DOWN TOGETHER",
};

function contactFrom(report: ContactReport, burning: boolean, destroyed: boolean): EnemyContact {
  return {
    id: report.id,
    range: formatRangeYards(report.rangeM),
    rangeM: report.rangeM,
    clock: clockLabel(report.clockPosition),
    relativeHeight: relativeHeightLabel(report.heightDifferenceM),
    bearingDeg: radiansToDegrees(report.relativeBearingRad),
    behind: report.behind,
    closureMph: mpsToMph(report.closureMps),
    burning,
    destroyed,
  };
}

/** Height difference as an altimeter-style string, for a debrief or a tooltip. */
export function formatHeightDifference(heightDifferenceM: number): string {
  const feet = Math.abs(heightDifferenceM) / 0.3048;
  return `${relativeHeightLabel(heightDifferenceM)} ${formatAltitudeFt(feet)} ft`;
}

/** The nearest hostile that has not already hit the ground, or the last one if all have. */
function trackedEnemy(state: EncounterState, playerPosition: Vec3) {
  const hostiles = state.aircraft.filter((entry) => entry.side === "hostile");
  const live = hostiles.filter((entry) => entry.status !== "destroyed");
  const candidates = live.length > 0 ? live : hostiles;
  let best = candidates[0];
  let bestRange = Number.POSITIVE_INFINITY;
  for (const entry of candidates) {
    const range = Math.hypot(
      entry.sim.position.x - playerPosition.x,
      entry.sim.position.y - playerPosition.y,
      entry.sim.position.z - playerPosition.z,
    );
    if (range < bestRange) {
      best = entry;
      bestRange = range;
    }
  }
  return best;
}

export function combatHudFrom(state: EncounterState, playerId: string): CombatHud {
  const player = state.aircraft.find((entry) => entry.id === playerId);
  const enemy = player ? trackedEnemy(state, player.sim.position) : undefined;
  const damage = player?.sim.damage;
  return {
    enemy:
      player && enemy
        ? contactFrom(
            contactReport(enemy.id, player.sim, enemy.sim),
            enemy.fire.burning,
            enemy.status === "destroyed",
          )
        : undefined,
    status: state.status,
    banner: OUTCOME_BANNERS[state.status],
    airframe: damage
      ? Math.min(damage.engine, damage.wingLeft, damage.wingRight, damage.controls, damage.fuel)
      : 1,
    wounded: player?.sim.pilot.wounded ?? false,
    burning: player?.fire.burning ?? false,
    hitsScored: player?.hitsScored ?? 0,
    hitsTaken: player?.hitsTaken ?? 0,
    gunHitsTaken: player?.gunHitsTaken ?? 0,
    bombs: bombLoadOf(player),
  };
}

/**
 * The damage readout, or `undefined` while nothing has happened yet (G34/B6:
 * `CombatHud` had computed all of this since it was written and the overlay drew
 * none of it — there was no damage indicator anywhere in flight).
 *
 * **Port-side, and shaped to stay that way.** The original has no damage panel:
 * it tells the pilot by message (`Your wings are shaking...`, `Your oil pressure
 * is dropping!`, `You feel faint.` — G33 wires those) and by the oil gauge going
 * over. So this readout is not on screen unless something has actually happened
 * — one hit taken, one hit scored, a wound, a fire, or a component off 100 % —
 * which keeps the clean aeroplane's HUD exactly as authentic as it was.
 *
 * `airframe` is already the *worst* component, not an average
 * (`combatHudFrom`), so the number is the one the pilot needs: how close the
 * nearest thing to failing is.
 */
export interface DamageReport {
  /** 0–100, rounded. The worst component, not a mean. */
  readonly airframePercent: number;
  /** `WOUNDED`, `ON FIRE` — the states that are not a matter of degree. */
  readonly tags: readonly string[];
  readonly hitsTaken: number;
  readonly hitsScored: number;
  /** Below a quarter, or wounded, or burning: the panel goes red. */
  readonly critical: boolean;
}

/** Below this the readout is drawn as a warning rather than a reading. */
export const CRITICAL_AIRFRAME = 0.25;

export function damageReportOf(hud: CombatHud): DamageReport | undefined {
  const untouched = hud.airframe >= 1 && !hud.wounded && !hud.burning && hud.hitsTaken === 0 && hud.hitsScored === 0;
  if (untouched) return undefined;
  const tags = [hud.wounded ? "WOUNDED" : "", hud.burning ? "ON FIRE" : ""].filter((tag) => tag !== "");
  return {
    // `Math.floor`, not `Math.round`: a wing at 0.4 % left must not read 0 %
    // *or* 100 %, and rounding down means the readout only ever understates how
    // much aeroplane is left, which is the safe direction to be wrong in.
    airframePercent: Math.max(0, Math.min(100, Math.floor(hud.airframe * 100))),
    tags,
    hitsTaken: hud.hitsTaken,
    hitsScored: hud.hitsScored,
    critical: hud.airframe < CRITICAL_AIRFRAME || hud.wounded || hud.burning,
  };
}

function bombLoadOf(player: EncounterState["aircraft"][number] | undefined): BombLoadHud | undefined {
  if (!player) return undefined;
  const carried = player.bombsRemaining + player.bombsReleased;
  if (carried <= 0) return undefined;
  return { remaining: player.bombsRemaining, released: player.bombsReleased };
}
